import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {Validators, FormGroup, FormControl, AbstractControl, ValidationErrors} from '@angular/forms';
import {forkJoin} from 'rxjs';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {WebService} from '../../services/web/web.service';
import {ToastService} from '../../services/notification/toast.service';
import {SpinnerService} from '../../services/spinner/spinner.service';
import {Router} from '@angular/router';
import {SecurityService} from "../../services/security.service";
import {WebAuthnService} from '../../services/webauthn.service';
import {PasskeyService} from '../../services/passkey.service';
import {LoggerService} from '../../services/logger.service';
import {PasskeyCredential} from '../../models/passkey.model';
import {environment} from '../../../environments/environment';
import {SessionStoreService} from '../../services/session-store.service';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {TranslateService} from '@ngx-translate/core';
import {
  SendgridPartnerConfiguration,
  SendgridSlotAssignment,
  SendgridSlotDefinition,
  SendgridTemplate,
  SendgridTemplatesService,
  SendgridTemplateVersion,
  SendgridVerifiedSender
} from '../../services/sendgrid-templates.service';

@Component({
    selector: 'app-configurations',
    templateUrl: './configurations.component.html',
    styleUrls: ['./configurations.component.scss'],
    standalone: false
})
export class ConfigurationsComponent implements OnInit {
  @ViewChild('sendgridTemplatePickerDialog') sendgridTemplatePickerDialog!: TemplateRef<any>;
  @ViewChild('sendgridSenderPickerDialog') sendgridSenderPickerDialog!: TemplateRef<any>;
  @ViewChild('sendgridVersionPickerDialog') sendgridVersionPickerDialog!: TemplateRef<any>;
  @ViewChild('sendgridChangeTemplateDialog') sendgridChangeTemplateDialog!: TemplateRef<any>;
  @ViewChild('sendgridPreviewDialog') sendgridPreviewDialog!: TemplateRef<any>;

  form: FormGroup;
  public username: string = '';
  public partner: any;

  provincesList: any[] = [];
  provincesGeneralList: any[] = [];
  corregimientos: { [key: string]: any[] } = {};
  corregimientosSelected: any[] = [];
  emailPaymentSucceeded!: boolean;
  emailChangeMethodPayment: boolean;
  whatsappChangeMethodPayment: boolean;
  emailPaymentFailed!: boolean;
  notifyCancelledSubscription: boolean;
  myPortalsDefaultTab: 'services_first' | 'kpi_first' | null = null;
  settings : any;
  partnerSetting : any;

  corregimientosToSend : any[] = [];
  previousValuesToSend : any[] = [];

  // ═══════════════════════════════════════════════════════════════════
  // PROPIEDADES PARA GESTIÓN DE PASSKEYS
  // ═══════════════════════════════════════════════════════════════════
  
  /**
   * Lista de passkeys registradas del usuario
   */
  passkeys: PasskeyCredential[] = [];

  /**
   * Indica si se están cargando las passkeys
   */
  isLoadingPasskeys: boolean = false;

  /**
   * Indica si el navegador soporta WebAuthn
   */
  webAuthnSupported: boolean = false;

  /**
   * Indica si el usuario tiene al menos una passkey
   */
  hasPasskeys: boolean = false;

  /**
   * Indica si se está registrando una nueva passkey
   */
  isRegisteringPasskey: boolean = false;

  /**
   * Mensaje de error al gestionar passkeys
   */
  passkeyError: string | null = null;

  /**
   * Nombre del método biométrico disponible
   */
  biometricMethodName: string = 'Passkey';

  /**
   * Logger para debugging
   */
  private logger: LoggerService;

  /**
   * Índice del tab activo (0-3)
   * 0 = Provincias y Corregimientos
   * 1 = Otras Configuraciones
   * 2 = Notificaciones
   * 3 = Gestión de Passkeys
   * 4 = Partners (solo super admin)
   */
  selectedTabIndex: number = 0;
  isSuperAdmin = false;
  createPartnerTabEnabled = environment.createPartnerTabEnabled;

  sendgridSlots: SendgridSlotDefinition[] = [];
  sendgridVerifiedSenders: SendgridVerifiedSender[] = [];
  filteredSendgridVerifiedSenders: SendgridVerifiedSender[] = [];
  sendgridTemplates: SendgridTemplate[] = [];
  filteredSendgridTemplates: SendgridTemplate[] = [];
  sendgridVersions: SendgridTemplateVersion[] = [];
  sendgridConfiguration: SendgridPartnerConfiguration | null = null;
  originalSendgridConfiguration: SendgridPartnerConfiguration | null = null;
  currentSendgridTemplate: SendgridTemplate | null = null;
  selectedSenderCandidate: SendgridVerifiedSender | null = null;
  selectedTemplateCandidate: SendgridTemplate | null = null;
  selectedSlot: SendgridSlotDefinition | null = null;
  selectedVersionCandidate: SendgridTemplateVersion | null = null;
  previewVersion: SendgridTemplateVersion | null = null;
  previewHtml: SafeHtml | null = null;
  sendgridSenderSearch = '';
  sendgridTemplateSearch = '';
  sendgridLoading = false;
  sendgridVersionsLoading = false;
  sendgridSaving = false;
  sendgridError: string | null = null;
  private sendgridTemplateDialogRef: MatDialogRef<any> | null = null;
  private sendgridSenderDialogRef: MatDialogRef<any> | null = null;
  private sendgridVersionDialogRef: MatDialogRef<any> | null = null;
  private sendgridChangeTemplateDialogRef: MatDialogRef<any> | null = null;
  private sendgridPreviewDialogRef: MatDialogRef<any> | null = null;

  get isPartnersTabActive(): boolean {
    return this.isSuperAdmin && this.createPartnerTabEnabled && this.selectedTabIndex === 5;
  }

    constructor(
        private securityService: SecurityService,
        private spinner: SpinnerService,
        private webService: WebService,
        private toast: ToastService,
        private router: Router,
        private dialog: MatDialog,
        private webAuthnService: WebAuthnService,
        private passkeyService: PasskeyService,
        private sessionStore: SessionStoreService,
        private sendgridTemplatesService: SendgridTemplatesService,
        private translate: TranslateService,
        private sanitizer: DomSanitizer
    ) {
        this.form = this.updateFormGroup();
        this.logger = new LoggerService();
        this.sendgridSlots = this.sendgridTemplatesService.slots;
    }

  async ngOnInit(): Promise<void> {
        this.isSuperAdmin = this.securityService.isSuperAdmin();
        this.username = this.securityService.getTokenSub() || '';

        // Inicializar gestión de passkeys
        await this.initializePasskeyManagement();

        if (this.securityService.getTokenPartner() != null || this.securityService.getTokenPartner() != undefined) {
            this.partner = this.securityService.getTokenPartner(); // TOMAMOS EL PARTNER EN SESION
            this.loadPartnerSettings();
            this.loadSendgridTemplates();
            this.spinner.open();
            this.webService.get(this.webService.HOST + "/province").subscribe(response => {
                this.provincesList = response.result; // CARGAMOS TODAS LAS PROVINCIAS ACTIVAS DEL SISTEMA
                this.provincesGeneralList = response.result;
                this.webService.get(this.webService.HOST + "/partner/" + this.partner).subscribe(partner => {
                    console.log(partner);
                    // PROCEDEMOS A SETEAR EN PANTALLA LAS PROVINCIAS QUE TIENE ESE PARTNER ASOCIADAS EN LA TABLA PROVINCES PARTNERS
                    this.form.controls["provinces"].setValue(this.provincesList.filter(item1 => partner.result.provinces.some(item2 => item1.id === item2.id)));
                    // AQUI VAMOS A RECORRER Y ARMAR UN NUEVO ARREGLO QUE CONTENDRÁ LOS CORREGIMIENTOS QUE TIENE ESE PARTNER ASOCIADOS
                    partner.result.partnerCorregimientos.forEach(element => {
                        this.corregimientosSelected.push(element.corregimiento);
                    });
                    // AQUI LO QUE HACEMOS ES DETERMINAR SI DEBEMOS PRESELECCIONAR TODOS LOS CORREGIMIENTOS EN PANTALLA O NO
                    const preSelectCorregimientos = this.corregimientosSelected.length !== 0;
                    this.onProvinceChange(preSelectCorregimientos);
                });
            }, err => {
                this.spinner.close();
                console.log(err);
            });
        } else {
            this.toast.showInfo("Por favor, creación el proceso de creacion de su cuenta")
        }

    }

  onProvinceChange(selectAll: boolean) {
    this.corregimientos = {}; // LIMPIAMOS EL ARREGLO EN PANTALLA
    // RECORRER TODAS LAS PROVINCIAS ASIGNADAS AL PARTNER Y OBTENER LOS CORREGIMIENTOS ASOCIADOS
    this.form.value.provinces.forEach((p: any) => {
      const provinceCorregimientos = this.provincesGeneralList.find(t => t.id === p.id)?.corregimientos || [];
      this.corregimientos[p.id] = provinceCorregimientos.sort((a: any, b: any) => (a.name > b.name ? 1 : -1));
    });

    if (selectAll) {
      const allCorregimientos: any[] = Object.values(this.corregimientos).reduce((acc: any[], val: any[]) => acc.concat(val), []);
      this.form.controls["corregimientos"].setValue(allCorregimientos);
    } else {
      const selectedCorregimientos: any[] = Object.values(this.corregimientos)
        .reduce((acc: any[], val: any[]) => acc.concat(val), [])
        .filter((item1: any) => this.corregimientosSelected.some((item2: any) => item1.id === item2.id));
      this.form.controls["corregimientos"].setValue(selectedCorregimientos);
    }
    this.spinner.close();
  }

  onCorregimientosChange(selectedValues: any, provinceId: any): void {
    // Handle the change event here
    let arraySameProvince: any[] = [];
    this.corregimientosSelected.forEach(selected => {
      arraySameProvince.push(...this.corregimientos[provinceId].filter(item => item.id == selected.id));
    });
    // Function to check if an element exists in an array
    const existsInArray = (array: any[], element: any) => {
      return array.some(item => item.id === element.id);
    };
    // Find elements in arraySameProvince that are not in selectedValues
    let differentElements = arraySameProvince.filter(item => !existsInArray(selectedValues, item));

    // Remove differentElements from this.corregimientosSelected
    this.corregimientosSelected = this.corregimientosSelected.filter(item => 
      !existsInArray(differentElements, item)
    );

    this.previousValuesToSend.push(...selectedValues)
    this.previousValuesToSend.forEach(c => {
      if(c.province == null || c.province == undefined){
        c.province = provinceId
      }
    });
    this.previousValuesToSend = Array.from(new Set(this.previousValuesToSend.map(item => JSON.stringify(item))))
                                     .map(item => JSON.parse(item));

                                      // Filter out items that do not match the provinceId or are not in selectedValues
    this.previousValuesToSend = this.previousValuesToSend.filter(item => 
      item.province === provinceId && selectedValues.some(val => val.id === item.id)
  );

    if(this.corregimientosToSend.length == 0){
      this.corregimientosToSend.push(...JSON.parse(JSON.stringify(this.previousValuesToSend)));
    }else{
      
      this.previousValuesToSend.forEach(c =>{
        this.corregimientosToSend.push(c)
      });
    
      // Remove duplicates
      this.corregimientosToSend = Array.from(new Set(this.corregimientosToSend.map(item => JSON.stringify(item))))
      .map(item => JSON.parse(item));

      let temporaryArray = JSON.parse(JSON.stringify(this.corregimientosToSend.filter(item => 
        item.province !== provinceId)));
    
      // Filter out items that do not match the provinceId or are not in selectedValues
      this.corregimientosToSend = this.previousValuesToSend.filter(item => 
        item.province === provinceId && selectedValues.some(val => val.id === item.id));

      this.corregimientosToSend.push(...temporaryArray);

    }
  }

  updateFormGroup(): FormGroup {
    return new FormGroup({
      provinces: new FormControl(''),
      corregimientos: new FormControl('')
    });
  }

  // Función personalizada de validación
  passwordsMatchValidator(passwordField: AbstractControl): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.value === passwordField.value ? null : { 'mismatch': true };
    };
  }

  send() {
    const userId = this.securityService.getTokenAdminOrUser();
    if (userId == null) {
      this.toast.showError("No se pudo identificar el usuario de la sesi\u00f3n");
      return;
    }
    this.spinner.open();
    this.sendConfigurations();

    let provincesToSend: any[] = [];
    this.form.value.provinces.forEach((province: any) => {
      provincesToSend.push({ id: province.id });
    });

    this.corregimientosToSend.push(...this.corregimientosSelected);
    // Remove duplicates
    this.corregimientosToSend = Array.from(new Set(this.corregimientosToSend.map(item => JSON.stringify(item))))
    .map(item => JSON.parse(item));

    const json = {
      provinces: provincesToSend,
      corregimientos: this.corregimientosToSend.length == 0 ? this.corregimientosSelected : this.corregimientosToSend,
      partner: this.partner,
      user: userId
    };
    this.webService.put(json, this.webService.HOST + "/onboarding/partner").subscribe(response => {
      this.spinner.close();
      this.toast.showSuccess("Información actualizada con éxito");
      // Reload the page by navigating to the same route
      this.router.navigate([this.router.url]);
    }, err => {
      this.spinner.close();
      this.toast.showError("Error al actualizar la información");
      console.error(err);
    });
  }

  goBack() {
    if (!this.confirmDiscardSendgridChanges()) {
      return;
    }
    this.router.navigate(["/my-portals"]);
  }

  removeProvince(province: any) {
    const currentProvinces = this.form.controls['provinces'].value.filter((p: any) => p.id !== province.id);
    this.form.controls['provinces'].setValue(currentProvinces);
    this.onProvinceChange(false);
  }




    // Método para cargar las configuraciones del Partner
    loadPartnerSettings(): void {
        this.webService.get(this.webService.HOST + "/partnerSetting/partner/" + this.partner).subscribe(partnerSettings =>{
            this.partnerSetting = partnerSettings.result;
            this.emailPaymentSucceeded = partnerSettings.result.emailPaymentSucceeded;
            this.emailPaymentFailed = partnerSettings.result.emailPaymentFailed;
            this.emailChangeMethodPayment = partnerSettings.result.emailChangeMethodPayment;
            this.whatsappChangeMethodPayment = partnerSettings.result.whatsappChangeMethodPayment;
            this.notifyCancelledSubscription = partnerSettings.result.notifyCancelledSubscription;
            this.myPortalsDefaultTab = this.normalizeMyPortalsDefaultTab(partnerSettings.result.myPortalsDefaultTab);
        }, err =>{
          console.error(err);
          this.toast.showError(err)
        })
}

sendConfigurations(){
  this.partnerSetting.emailPaymentSucceeded = this.emailPaymentSucceeded;
  this.partnerSetting.emailPaymentFailed = this.emailPaymentFailed;
  this.partnerSetting.emailChangeMethodPayment = this.emailChangeMethodPayment;
  this.partnerSetting.whatsappChangeMethodPayment = this.whatsappChangeMethodPayment;
  this.partnerSetting.notifyCancelledSubscription = this.notifyCancelledSubscription;
  this.partnerSetting.myPortalsDefaultTab = this.myPortalsDefaultTab;
  this.partnerSetting.partner = {id : this.partnerSetting.partner.id}

  delete this.partnerSetting.createdAt;
  delete this.partnerSetting.updatedAt;

  this.webService.put(this.partnerSetting,this.webService.HOST + "/partnerSetting/" + this.partnerSetting.id).subscribe(response =>{

  }, err =>{
    console.error(err);
    this.toast.showError(err);
  })
}

normalizeMyPortalsDefaultTab(value: unknown): 'services_first' | 'kpi_first' | null {
  return value === 'services_first' || value === 'kpi_first' ? value : null;
}

onToggleChange(): void {
  const updatedSettings = {
    partner_settings: {
      email_payment_succeeded: this.emailPaymentSucceeded,
      email_payment_failed: this.emailPaymentFailed,
      email_change_method_payment : this.emailChangeMethodPayment,
      whatsapp_change_method_payment : this.whatsappChangeMethodPayment
    }
  };
}
  goSettings(){
  if (!this.confirmDiscardSendgridChanges()) {
    return;
  }
  this.router.navigate(['/settings'], { queryParams: { new: true } });
}

// ═══════════════════════════════════════════════════════════════════
// MÉTODOS PARA SENDGRID TEMPLATES
// ═══════════════════════════════════════════════════════════════════

loadSendgridTemplates(): void {
  if (!this.partner) {
    return;
  }

  this.sendgridLoading = true;
  this.sendgridError = null;

  forkJoin({
    templates: this.sendgridTemplatesService.listTemplates(),
    senders: this.sendgridTemplatesService.listVerifiedSenders()
  }).subscribe({
    next: ({ templates, senders }) => {
      this.sendgridTemplates = templates;
      this.filteredSendgridTemplates = templates;
      this.sendgridVerifiedSenders = senders;
      this.filteredSendgridVerifiedSenders = senders;
      this.loadSendgridConfiguration();
    },
    error: error => {
      this.sendgridLoading = false;
      this.handleSendgridError(error);
    }
  });
}

loadSendgridConfiguration(): void {
  this.sendgridTemplatesService.getPartnerConfiguration(this.partner).subscribe({
    next: configuration => {
      this.sendgridConfiguration = this.cloneSendgridConfiguration(configuration);
      this.originalSendgridConfiguration = this.cloneSendgridConfiguration(configuration);
      this.sendgridSlots = configuration.slots.map(slot => ({ key: slot.key, label: slot.label }));
      this.currentSendgridTemplate = this.getTemplateById(configuration.templateId);
      this.sendgridVersions = [];
      if (configuration.templateId) {
        this.loadSelectedSendgridTemplate(configuration.templateId);
      }
      this.sendgridLoading = false;
    },
    error: error => {
      this.sendgridLoading = false;
      this.handleSendgridError(error);
    }
  });
}

get hasSendgridTemplate(): boolean {
  return !!this.sendgridConfiguration?.templateId;
}

get currentSendgridSender(): SendgridVerifiedSender | null {
  const verifiedEmail = this.sendgridConfiguration?.verifiedEmail;
  if (!verifiedEmail) {
    return null;
  }

  return this.sendgridVerifiedSenders.find(sender => sender.fromEmail === verifiedEmail) || {
    id: verifiedEmail,
    nickname: verifiedEmail,
    fromEmail: verifiedEmail,
    fromName: '',
    replyTo: '',
    verified: true
  };
}

get hasSendgridDirtyChanges(): boolean {
  if (!this.sendgridConfiguration || !this.originalSendgridConfiguration) {
    return false;
  }

  return JSON.stringify(this.normalizeSendgridConfiguration(this.sendgridConfiguration)) !==
    JSON.stringify(this.normalizeSendgridConfiguration(this.originalSendgridConfiguration));
}

getSendgridSlotAssignment(slotKey: string): SendgridSlotAssignment | null {
  return this.sendgridConfiguration?.slots.find(slot => slot.key === slotKey) || null;
}

getSendgridSlotVersion(slotKey: string): SendgridTemplateVersion | null {
  const assignment = this.getSendgridSlotAssignment(slotKey);
  if (!assignment?.versionId || !this.sendgridConfiguration?.templateId) {
    return null;
  }

  return this.sendgridVersions.find(version => version.id === assignment.versionId) || null;
}

getSendgridSlotStatus(slotKey: string): string {
  const assignment = this.getSendgridSlotAssignment(slotKey);
  if (!assignment || !assignment.versionId) {
    return 'Sin asociar';
  }

  if (assignment.status === 'orphan') {
    return 'Reasignar';
  }

  return assignment.status === 'pending' ? 'Sin guardar' : 'Configurado';
}

getSendgridSlotStatusClass(slotKey: string): string {
  const assignment = this.getSendgridSlotAssignment(slotKey);
  if (assignment?.status === 'orphan') {
    return 'status-error';
  }
  if (!assignment?.versionId) {
    return 'status-empty';
  }
  return assignment.status === 'pending' ? 'status-pending' : 'status-configured';
}

openSenderPicker(): void {
  this.selectedSenderCandidate = this.currentSendgridSender;
  this.filterSendgridSenders();
  this.sendgridSenderDialogRef = this.dialog.open(this.sendgridSenderPickerDialog, {
    width: '720px',
    maxWidth: '92vw',
    autoFocus: false,
    restoreFocus: false
  });
}

closeSenderPicker(): void {
  this.sendgridSenderDialogRef?.close();
  this.sendgridSenderDialogRef = null;
  this.selectedSenderCandidate = null;
}

selectSenderCandidate(sender: SendgridVerifiedSender): void {
  if (!sender.verified) {
    return;
  }

  this.selectedSenderCandidate = sender;
}

applySelectedSender(): void {
  if (!this.selectedSenderCandidate || !this.sendgridConfiguration) {
    return;
  }

  this.sendgridConfiguration.verifiedEmail = this.selectedSenderCandidate.fromEmail;
  this.closeSenderPicker();
}

filterSendgridSenders(): void {
  const term = this.sendgridSenderSearch.trim().toLowerCase();
  this.filteredSendgridVerifiedSenders = !term
    ? this.sendgridVerifiedSenders
    : this.sendgridVerifiedSenders.filter(sender =>
        sender.nickname.toLowerCase().includes(term) ||
        sender.fromEmail.toLowerCase().includes(term) ||
        sender.fromName.toLowerCase().includes(term)
      );
}

openTemplatePicker(): void {
  this.selectedTemplateCandidate = this.currentSendgridTemplate;
  this.filterSendgridTemplates();
  this.sendgridTemplateDialogRef = this.dialog.open(this.sendgridTemplatePickerDialog, {
    width: '860px',
    maxWidth: '92vw',
    autoFocus: false,
    restoreFocus: false
  });
}

closeTemplatePicker(): void {
  this.sendgridTemplateDialogRef?.close();
  this.sendgridTemplateDialogRef = null;
  this.selectedTemplateCandidate = null;
}

selectTemplateCandidate(template: SendgridTemplate): void {
  this.selectedTemplateCandidate = template;
}

applySelectedTemplate(): void {
  if (!this.selectedTemplateCandidate || !this.sendgridConfiguration) {
    return;
  }

  if (this.currentSendgridTemplate && this.currentSendgridTemplate.id !== this.selectedTemplateCandidate.id && this.hasAssignedSendgridSlots()) {
    this.sendgridChangeTemplateDialogRef = this.dialog.open(this.sendgridChangeTemplateDialog, {
      width: '520px',
      maxWidth: '92vw',
      disableClose: true,
      autoFocus: false,
      restoreFocus: false
    });
    return;
  }

  this.applyTemplateChange(this.selectedTemplateCandidate);
}

confirmTemplateChange(): void {
  if (!this.selectedTemplateCandidate) {
    return;
  }

  this.applyTemplateChange(this.selectedTemplateCandidate);
  this.sendgridChangeTemplateDialogRef?.close();
  this.sendgridChangeTemplateDialogRef = null;
}

cancelTemplateChange(): void {
  this.sendgridChangeTemplateDialogRef?.close();
  this.sendgridChangeTemplateDialogRef = null;
}

openVersionPicker(slot: SendgridSlotDefinition): void {
  if (!this.sendgridConfiguration?.templateId) {
    this.toast.showInfo(this.translate.instant('configurations.sendgrid.feedback.chooseTemplateFirst'));
    return;
  }

  this.selectedSlot = slot;
  this.selectedVersionCandidate = this.getSendgridSlotVersion(slot.key);
  this.sendgridVersionsLoading = false;
  this.sendgridVersionDialogRef = this.dialog.open(this.sendgridVersionPickerDialog, {
    width: '760px',
    maxWidth: '92vw',
    autoFocus: false,
    restoreFocus: false
  });

  if (!this.sendgridVersions.length) {
    this.loadSelectedSendgridTemplate(this.sendgridConfiguration.templateId);
  }
}

closeVersionPicker(): void {
  this.sendgridVersionDialogRef?.close();
  this.sendgridVersionDialogRef = null;
  this.selectedSlot = null;
  this.selectedVersionCandidate = null;
}

selectVersionCandidate(version: SendgridTemplateVersion): void {
  this.selectedVersionCandidate = version;
}

applySelectedVersion(): void {
  if (!this.selectedSlot || !this.selectedVersionCandidate || !this.sendgridConfiguration) {
    return;
  }

  const assignment = this.sendgridConfiguration.slots.find(slot => slot.key === this.selectedSlot?.key);
  if (assignment) {
    assignment.versionId = this.selectedVersionCandidate.id;
    assignment.status = 'pending';
  }

  this.closeVersionPicker();
}

openPreview(version: SendgridTemplateVersion | null): void {
  if (!version) {
    return;
  }

  this.previewVersion = version;
  this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(version.html || '');
  this.sendgridPreviewDialogRef = this.dialog.open(this.sendgridPreviewDialog, {
    width: '980px',
    maxWidth: '94vw',
    autoFocus: false,
    restoreFocus: false
  });
}

closePreview(): void {
  this.sendgridPreviewDialogRef?.close();
  this.sendgridPreviewDialogRef = null;
  this.previewVersion = null;
  this.previewHtml = null;
}

onConfigurationsTabChange(index: number): void {
  if (this.selectedTabIndex === 3 && index !== 3 && !this.confirmDiscardSendgridChanges()) {
    this.selectedTabIndex = 3;
    return;
  }

  this.selectedTabIndex = index;
}

saveActiveConfigurations(): void {
  if (this.selectedTabIndex === 3) {
    this.saveSendgridConfiguration();
    return;
  }

  this.send();
}

saveSendgridConfiguration(): void {
  if (!this.partner || !this.sendgridConfiguration) {
    return;
  }

  this.sendgridSaving = true;
  this.spinner.open();

  if (!this.hasSendgridDirtyChanges) {
    this.sendgridSaving = false;
    this.spinner.close();
    this.toast.showInfo(this.translate.instant('configurations.sendgrid.feedback.noChanges'));
    return;
  }

  this.sendgridTemplatesService.savePartnerConfiguration(this.partner, this.sendgridConfiguration).subscribe({
    next: saved => {
      this.sendgridConfiguration = this.cloneSendgridConfiguration(saved);
      this.originalSendgridConfiguration = this.cloneSendgridConfiguration(saved);
      this.sendgridSlots = saved.slots.map(slot => ({ key: slot.key, label: slot.label }));
      this.currentSendgridTemplate = this.getTemplateById(saved.templateId);
      this.sendgridSaving = false;
      this.spinner.close();
      this.toast.showSuccess(this.translate.instant('configurations.sendgrid.feedback.saveSuccess'));
    },
    error: error => {
      this.sendgridSaving = false;
      this.spinner.close();
      if (this.isAuthError(error)) {
        sessionStorage.setItem('sendgrid_templates_draft', JSON.stringify(this.sendgridConfiguration));
        this.router.navigate(['/login']);
        return;
      }
      this.handleSendgridError(error);
    }
  });
}

filterSendgridTemplates(): void {
  const term = this.sendgridTemplateSearch.trim().toLowerCase();
  this.filteredSendgridTemplates = !term
    ? this.sendgridTemplates
    : this.sendgridTemplates.filter(template => template.name.toLowerCase().includes(term) || template.id.toLowerCase().includes(term));
}

private applyTemplateChange(template: SendgridTemplate): void {
  if (!this.sendgridConfiguration) {
    return;
  }

  this.sendgridConfiguration.templateId = template.id;
  this.currentSendgridTemplate = template;
  this.sendgridConfiguration.slots = this.sendgridSlots.map(slot => ({
    key: slot.key,
    label: slot.label,
    versionId: null,
    status: 'empty'
  }));
  this.sendgridVersions = [];
  this.closeTemplatePicker();
  this.loadSelectedSendgridTemplate(template.id);
}

private loadSelectedSendgridTemplate(templateId: string): void {
  this.sendgridVersionsLoading = true;
  this.sendgridTemplatesService.getTemplate(templateId).subscribe({
    next: template => {
      this.currentSendgridTemplate = template;
      this.sendgridVersions = template.versions;
      this.sendgridVersionsLoading = false;
    },
    error: error => {
      this.sendgridVersionsLoading = false;
      this.handleSendgridError(error);
    }
  });
}

private hasAssignedSendgridSlots(): boolean {
  return !!this.sendgridConfiguration?.slots.some(slot => !!slot.versionId);
}

private getTemplateById(templateId: string | null): SendgridTemplate | null {
  if (!templateId) {
    return null;
  }
  return this.sendgridTemplates.find(template => template.id === templateId) || null;
}

private normalizeSendgridConfiguration(configuration: SendgridPartnerConfiguration): any {
  return {
    templateId: configuration.templateId,
    verifiedEmail: configuration.verifiedEmail,
    slots: configuration.slots.map(slot => ({ key: slot.key, versionId: slot.versionId }))
  };
}

private cloneSendgridConfiguration(configuration: SendgridPartnerConfiguration): SendgridPartnerConfiguration {
  return JSON.parse(JSON.stringify(configuration));
}

private handleSendgridError(error: any, showToast = true): void {
  const message = typeof error === 'string' ? error : this.translate.instant('configurations.sendgrid.feedback.error');
  this.sendgridError = message;
  if (showToast) {
    this.toast.showError(message);
  }
}

private isAuthError(error: any): boolean {
  return error?.status === 401 || error?.status === 403 || String(error).toLowerCase().includes('sesión');
}

private confirmDiscardSendgridChanges(): boolean {
  if (this.selectedTabIndex !== 3 || !this.hasSendgridDirtyChanges) {
    return true;
  }

  return confirm(this.translate.instant('configurations.sendgrid.feedback.unsavedChanges'));
}

// ═══════════════════════════════════════════════════════════════════
// MÉTODOS PARA GESTIÓN DE PASSKEYS
// ═══════════════════════════════════════════════════════════════════

/**
 * Inicializa la gestión de passkeys
 */
private async initializePasskeyManagement(): Promise<void> {
  this.logger.log('🔐 [CONFIGURATIONS] Inicializando gestión de passkeys...');
  
  // Verificar soporte de WebAuthn
  this.webAuthnSupported = this.webAuthnService.isWebAuthnSupported();
  
  if (!this.webAuthnSupported) {
    this.logger.warn('⚠️ [CONFIGURATIONS] WebAuthn no soportado');
    return;
  }

  this.logger.log('✅ [CONFIGURATIONS] WebAuthn soportado');

  // Obtener nombre del método biométrico
  try {
    this.biometricMethodName = await this.webAuthnService.getBiometricMethodName();
    this.logger.log('✅ [CONFIGURATIONS] Método biométrico:', this.biometricMethodName);
  } catch (error) {
    this.logger.error('❌ [CONFIGURATIONS] Error obteniendo método biométrico:', error);
  }

  // Cargar passkeys del usuario
  this.loadPasskeys();
}

/**
 * Carga la lista de passkeys del usuario
 */
loadPasskeys(): void {
  this.logger.log('📋 [CONFIGURATIONS] Cargando lista de passkeys...');
  this.logger.log('📋 [CONFIGURATIONS] URL del API:', `${environment.backendUrl}/auth/passkey/list`);
  this.isLoadingPasskeys = true;
  this.passkeyError = null;

  this.passkeyService.listPasskeys().subscribe({
    next: (passkeys) => {
      this.logger.log('✅ [CONFIGURATIONS] Respuesta recibida del backend');
      this.logger.log('✅ [CONFIGURATIONS] Passkeys recibidas:', passkeys);
      this.logger.log('✅ [CONFIGURATIONS] Cantidad de passkeys:', passkeys.length);
      
      this.passkeys = passkeys;
      this.hasPasskeys = passkeys.length > 0;
      this.isLoadingPasskeys = false;
    },
    error: (error) => {
      this.logger.error('❌ [CONFIGURATIONS] Error cargando passkeys:', error);
      this.logger.error('❌ [CONFIGURATIONS] Detalles del error:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        error: error.error
      });
      this.passkeyError = error.message || 'Error al cargar passkeys';
      this.isLoadingPasskeys = false;
    }
  });
}

/**
 * Registra una nueva passkey
 */
async registerNewPasskey(): Promise<void> {
  this.logger.log('🔐 [CONFIGURATIONS] Iniciando registro de nueva passkey...');
  this.isRegisteringPasskey = true;
  this.passkeyError = null;

  try {
    const email = this.securityService.getTokenSub();
    
    if (!email) {
      throw new Error('No se pudo obtener el email del usuario');
    }

    // Partner siempre es null
    const partner = null;
    
    this.logger.log('📡 [CONFIGURATIONS] Registrando passkey para:', email);
    
    // Ejecutar registro
    await this.passkeyService.registerPasskey(email, partner as any);
    
    this.logger.log('✅ [CONFIGURATIONS] Passkey registrada exitosamente');
    this.toast.showSuccess('Passkey registrada exitosamente');
    
    // Recargar lista de passkeys
    this.loadPasskeys();
    
  } catch (error: any) {
    this.logger.error('❌ [CONFIGURATIONS] Error registrando passkey:', error);
    
    const errorMessage = error.message || 'Error al registrar passkey';
    this.passkeyError = errorMessage;
    this.toast.showError(errorMessage);
    
  } finally {
    this.isRegisteringPasskey = false;
  }
}

/**
 * Elimina una passkey
 */
deletePasskey(passkey: PasskeyCredential): void {
  this.logger.log('🗑️ [CONFIGURATIONS] Iniciando eliminación de passkey:', passkey.credentialId);
  
  // Confirmar eliminación
  const confirmed = confirm(
    `¿Estás seguro de eliminar la passkey de "${passkey.deviceName}"?\n\n` +
    `Esta acción no se puede deshacer.`
  );
  
  if (!confirmed) {
    this.logger.log('ℹ️ [CONFIGURATIONS] Eliminación cancelada por el usuario');
    return;
  }

  this.spinner.open();
  this.passkeyError = null;

  this.logger.log('📡 [CONFIGURATIONS] Eliminando passkey:', passkey.credentialId);
  
  // Ejecutar eliminación
  this.passkeyService.deletePasskey(passkey.credentialId).subscribe({
    next: () => {
      this.logger.log('✅ [CONFIGURATIONS] Passkey eliminada exitosamente');
      this.toast.showSuccess('Passkey eliminada exitosamente');
      this.spinner.close();
      
      // Recargar lista de passkeys
      this.loadPasskeys();
    },
    error: (error) => {
      this.logger.error('❌ [CONFIGURATIONS] Error eliminando passkey:', error);
      
      const errorMessage = error.message || 'Error al eliminar passkey';
      this.passkeyError = errorMessage;
      this.toast.showError(errorMessage);
      this.spinner.close();
    }
  });
}

/**
 * Formatea la fecha de última vez usado
 */
getLastUsedText(lastUsedAt: Date | string | null): string {
  if (!lastUsedAt) {
    return 'Recién registrada';
  }

  const date = new Date(lastUsedAt);
  
  // Validar que la fecha sea válida
  if (isNaN(date.getTime())) {
    return 'Recién registrada';
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'Usado hace un momento';
  } else if (diffMins < 60) {
    return `Usado hace ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
  } else if (diffHours < 24) {
    return `Usado hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
  } else if (diffDays < 7) {
    return `Usado hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
  } else {
    return `Último uso: ${date.toLocaleDateString('es-ES', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })}`;
  }
}

/**
 * Obtiene el icono según el tipo de dispositivo
 */
getDeviceIcon(deviceName: string): string {
  const name = deviceName.toLowerCase();
  
  if (name.includes('iphone') || name.includes('ios')) {
    return 'phone_iphone';
  } else if (name.includes('ipad')) {
    return 'tablet_mac';
  } else if (name.includes('mac')) {
    return 'laptop_mac';
  } else if (name.includes('windows')) {
    return 'laptop_windows';
  } else if (name.includes('android')) {
    return 'phone_android';
  } else if (name.includes('chrome') || name.includes('firefox') || name.includes('safari')) {
    return 'computer';
  } else {
    return 'devices';
  }
}

/**
 * Elimina TODAS las passkeys del usuario.
 * 
 * Esta es una acción destructiva que vuelve al usuario a usar
 * PIN por email para todas las validaciones.
 * 
 * Requiere confirmación doble para evitar eliminaciones accidentales.
 */
async deleteAllPasskeys(): Promise<void> {
  this.logger.log('🗑️ [CONFIGURATIONS] Iniciando eliminación de TODAS las passkeys');
  
  // Primera confirmación
  const confirmed1 = confirm(
    '⚠️ ¿Estás seguro de eliminar TODAS tus passkeys?\n\n' +
    `Tienes ${this.passkeys.length} passkey${this.passkeys.length > 1 ? 's' : ''} registrada${this.passkeys.length > 1 ? 's' : ''}.\n\n` +
    'Volverás a usar el PIN por email para validar tu identidad.'
  );
  
  if (!confirmed1) {
    this.logger.log('ℹ️ [CONFIGURATIONS] Eliminación cancelada por el usuario (1ra confirmación)');
    return;
  }
  
  // Segunda confirmación
  const confirmed2 = confirm(
    '⚠️ ÚLTIMA CONFIRMACIÓN\n\n' +
    'Esta acción NO se puede deshacer.\n\n' +
    'Todas tus passkeys serán eliminadas permanentemente.\n\n' +
    '¿Continuar?'
  );
  
  if (!confirmed2) {
    this.logger.log('ℹ️ [CONFIGURATIONS] Eliminación cancelada por el usuario (2da confirmación)');
    return;
  }
  
  this.spinner.open();
  this.passkeyError = null;
  
  try {
    const email = this.securityService.getTokenSub();
    
    if (!email) {
      throw new Error('No se pudo obtener el email del usuario');
    }
    
    this.logger.log(`📡 [CONFIGURATIONS] Eliminando ${this.passkeys.length} passkeys...`);
    
    // Eliminar cada passkey
    let deletedCount = 0;
    let errorCount = 0;
    
    for (const passkey of this.passkeys) {
      try {
        await this.passkeyService.deletePasskey(passkey.credentialId).toPromise();
        deletedCount++;
        this.logger.log(`✅ [CONFIGURATIONS] Passkey eliminada: ${passkey.deviceName}`);
      } catch (error) {
        errorCount++;
        this.logger.error(`❌ [CONFIGURATIONS] Error eliminando passkey ${passkey.deviceName}:`, error);
      }
    }
    
    this.logger.log(`✅ [CONFIGURATIONS] Eliminación completada: ${deletedCount} exitosas, ${errorCount} errores`);
    
    if (errorCount === 0) {
      this.toast.showSuccess(
        `Todas las passkeys han sido eliminadas exitosamente. ` +
        `Ahora usarás el PIN por email para validar tu identidad.`
      );
    } else if (deletedCount > 0) {
      this.toast.showWarning(
        `Se eliminaron ${deletedCount} passkey${deletedCount > 1 ? 's' : ''}, ` +
        `pero ${errorCount} no pudieron ser eliminadas. Intenta nuevamente.`
      );
    } else {
      throw new Error('No se pudo eliminar ninguna passkey');
    }
    
    // Recargar lista (debería estar vacía o con las que fallaron)
    this.loadPasskeys();
    
  } catch (error: any) {
    this.logger.error('❌ [CONFIGURATIONS] Error eliminando todas las passkeys:', error);
    
    const errorMessage = error.message || 'Error al eliminar passkeys. Intenta nuevamente.';
    this.passkeyError = errorMessage;
    this.toast.showError(errorMessage);
    
  } finally {
    this.spinner.close();
  }
}

}
